/**
 * Builds a self-contained, full-screen slide-deck presentation (single HTML
 * document with inline CSS + JS) from the /company_performance payload.
 *
 * The returned string is written into a new window via document.write so it
 * can be presented immediately (arrow-key navigation, fullscreen) and printed
 * to PDF. It intentionally has no external dependencies so it renders in a
 * blank popup window or the Electron build.
 */

const BLUE = "#1565C0";
const TEAL = "#00897B";

function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Full peso amount, e.g. "₱1,711,561" or "-₱13,879" for negatives.
function peso(value) {
  const n = Math.round(Number(value) || 0);
  const sign = n < 0 ? "-" : "";
  return (
    sign +
    "₱" +
    Math.abs(n).toLocaleString("en-PH", { maximumFractionDigits: 0 })
  );
}

// Percentage or em-dash when not computable.
function pct(value) {
  return value === null || value === undefined ? "—" : `${value}%`;
}

// Money cell that reddens negatives (for profit/loss figures).
function money(value) {
  const cls = (Number(value) || 0) < 0 ? ' class="neg"' : "";
  return `<span${cls}>${peso(value)}</span>`;
}

// Compact peso for big headline numbers, e.g. "₱7.7M"
function pesoCompact(value) {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}₱${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}₱${(abs / 1_000).toFixed(0)}K`;
  return `${sign}₱${abs.toFixed(0)}`;
}

function num(value) {
  return Number(value || 0).toLocaleString("en-US");
}

// Growth badge HTML. null = no prior-year base to compare against.
function growthBadge(growth) {
  if (growth === null || growth === undefined) {
    return `<span class="badge badge-neutral">no prior-year data</span>`;
  }
  const up = growth >= 0;
  const arrow = up ? "▲" : "▼";
  const cls = up ? "badge-up" : "badge-down";
  return `<span class="badge ${cls}">${arrow} ${Math.abs(growth)}%</span>`;
}

function titleSlide(data) {
  const g =
    data.comparison && data.comparison.total_revenue
      ? data.comparison.total_revenue.growth
      : null;
  const hero =
    g === null
      ? `<div class="hero-growth neutral">Overall revenue growth: <b>no prior-year data to compare</b></div>`
      : `<div class="hero-growth ${g >= 0 ? "up" : "down"}">
           <span class="hero-num">${g >= 0 ? "▲" : "▼"} ${Math.abs(g)}%</span>
           <span class="hero-cap">overall revenue growth vs. previous 12 months</span>
         </div>`;

  return `
    <section class="slide slide-title">
      <div class="brand">JMB Computers</div>
      <h1>Company Performance</h1>
      <div class="subtitle">Last 12 Months${
        data.branch ? " &middot; " + escapeHtml(data.branch) : ""
      }</div>
      <div class="period">${escapeHtml(data.from_date)} &ndash; ${escapeHtml(
    data.to_date
  )}</div>
      ${hero}
      <div class="hint">Use &larr; &rarr; arrow keys to navigate &middot; press <b>F</b> for fullscreen</div>
    </section>`;
}

function kpiSlide(data) {
  const t = data.totals;
  const c = data.comparison || {};
  const card = (label, value, sub, mod) => `
    <div class="kpi ${mod || ""}">
      <div class="kpi-label">${label}</div>
      <div class="kpi-value">${value}</div>
      <div class="kpi-sub">${sub || ""}</div>
    </div>`;

  const netMod = (Number(t.net_income) || 0) < 0 ? "kpi-loss" : "kpi-profit";

  return `
    <section class="slide">
      <h2>Headline Numbers</h2>
      <div class="kpi-grid kpi-grid-4">
        ${card(
          "Net Sales",
          pesoCompact(t.total_revenue),
          c.total_revenue ? "YoY " + growthBadge(c.total_revenue.growth) : "",
          "kpi-strong"
        )}
        ${card(
          "Net Income",
          pesoCompact(t.net_income),
          `${pct(t.net_margin)} margin ${
            c.net_income
              ? "&middot; YoY " + growthBadge(c.net_income.growth)
              : ""
          }`,
          netMod
        )}
        ${card(
          "Gross Profit",
          pesoCompact(t.gross_profit),
          `${pct(t.gross_margin)} margin`
        )}
        ${card(
          "COGS + Expenses",
          pesoCompact(
            (Number(t.cogs) || 0) + (Number(t.operating_expenses) || 0)
          ),
          "cost of sales + opex"
        )}
        ${card(
          "Item Sales",
          pesoCompact(t.sales_revenue),
          c.sales_revenue ? "YoY " + growthBadge(c.sales_revenue.growth) : ""
        )}
        ${card(
          "Service Revenue",
          pesoCompact(t.service_revenue),
          c.service_revenue
            ? "YoY " + growthBadge(c.service_revenue.growth)
            : ""
        )}
        ${card(
          "Other Revenue",
          pesoCompact(t.other_revenue),
          c.other_revenue
            ? "YoY " + growthBadge(c.other_revenue.growth)
            : "incl. tax markup"
        )}
        ${card(
          "Collections",
          pesoCompact(t.collections),
          c.collections ? "YoY " + growthBadge(c.collections.growth) : ""
        )}
      </div>
      <div class="callout">
        Best month: <b>${escapeHtml(data.best_month || "—")}</b>
        &middot; Avg net sales / month: <b>${pesoCompact(
          t.avg_monthly_revenue
        )}</b>
        &middot; Invoices: <b>${num(t.invoices_count)}</b>
        &middot; Repairs: <b>${num(t.repairs_completed)}</b>
        &middot; Quotations: <b>${num(t.quotations_count)}</b>
      </div>
    </section>`;
}

function chartSlide(data) {
  const months = data.months || [];
  const max = Math.max(
    1,
    ...months.map((m) =>
      Math.max(m.total_revenue || 0, m.prev_year_total_revenue || 0)
    )
  );
  const bars = months
    .map((m) => {
      const curH = ((m.total_revenue || 0) / max) * 100;
      const prevH = ((m.prev_year_total_revenue || 0) / max) * 100;
      const shortLabel = escapeHtml(m.label.split(" ")[0]);
      return `
        <div class="bar-group" title="${escapeHtml(m.label)}: ${peso(
        m.total_revenue
      )}">
          <div class="bars">
            <div class="bar bar-prev" style="height:${prevH}%"></div>
            <div class="bar bar-cur" style="height:${curH}%"></div>
          </div>
          <div class="bar-label">${shortLabel}</div>
        </div>`;
    })
    .join("");

  return `
    <section class="slide">
      <h2>Revenue Trend</h2>
      <div class="legend">
        <span><i class="swatch" style="background:${BLUE}"></i> This period</span>
        <span><i class="swatch" style="background:#BBDEFB"></i> Same month last year</span>
      </div>
      <div class="chart">${bars}</div>
      <div class="chart-caption">Net sales per month, with prior-year comparison.</div>
    </section>`;
}

function yoySlide(data) {
  const rows = (data.months || [])
    .map((m) => {
      return `
        <tr>
          <td class="lbl">${escapeHtml(m.label)}</td>
          <td class="r">${peso(m.total_revenue)}</td>
          <td class="r muted">${peso(m.prev_year_total_revenue)}</td>
          <td class="r">${growthBadge(m.revenue_growth)}</td>
        </tr>`;
    })
    .join("");

  const c = data.comparison || {};
  const periodRow = (label, block) =>
    block
      ? `<tr>
          <td class="lbl">${label}</td>
          <td class="r">${peso(block.current)}</td>
          <td class="r muted">${peso(block.previous)}</td>
          <td class="r">${growthBadge(block.growth)}</td>
        </tr>`
      : "";

  return `
    <section class="slide">
      <h2>Year-over-Year Comparison</h2>
      <p class="sub">Each month against the same month one year earlier.</p>
      <table class="cmp">
        <thead>
          <tr><th>Month</th><th class="r">This Year</th><th class="r">Last Year</th><th class="r">Change</th></tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          ${periodRow("Total Revenue (12 mo)", c.total_revenue)}
          ${periodRow("Collections (12 mo)", c.collections)}
        </tfoot>
      </table>
    </section>`;
}

function profitabilitySlide(data) {
  const t = data.totals;
  const c = data.comparison || {};

  const rows = (data.months || [])
    .map(
      (m) => `
      <tr>
        <td class="lbl">${escapeHtml(m.label)}</td>
        <td class="r">${peso(m.total_revenue)}</td>
        <td class="r muted">${peso(m.cogs)}</td>
        <td class="r">${money(m.gross_profit)}</td>
        <td class="r muted">${peso(m.operating_expenses)}</td>
        <td class="r strong">${money(m.net_income)}</td>
        <td class="r">${pct(m.net_margin)}</td>
      </tr>`
    )
    .join("");

  // Period income-statement strip.
  const stat = (label, value, sub, mod) => `
    <div class="pl-stat ${mod || ""}">
      <div class="pl-stat-label">${label}</div>
      <div class="pl-stat-value">${value}</div>
      <div class="pl-stat-sub">${sub || ""}</div>
    </div>`;
  const netMod = (Number(t.net_income) || 0) < 0 ? "loss" : "profit";

  return `
    <section class="slide">
      <h2>Profitability</h2>
      <div class="pl-strip">
        ${stat("Net Sales", peso(t.total_revenue), "revenue &minus; discounts")}
        <div class="pl-op">&minus;</div>
        ${stat("COGS", peso(t.cogs), "cost of sales")}
        <div class="pl-op">=</div>
        ${stat(
          "Gross Profit",
          peso(t.gross_profit),
          pct(t.gross_margin) + " margin"
        )}
        <div class="pl-op">&minus;</div>
        ${stat(
          "Op. Expenses",
          peso(t.operating_expenses),
          "operating + direct"
        )}
        <div class="pl-op">=</div>
        ${stat(
          "Net Income",
          peso(t.net_income),
          `${pct(t.net_margin)} margin ${
            c.net_income
              ? "&middot; YoY " + growthBadge(c.net_income.growth)
              : ""
          }`,
          netMod
        )}
      </div>
      <table class="cmp small">
        <thead>
          <tr>
            <th>Month</th><th class="r">Net Sales</th><th class="r">COGS</th>
            <th class="r">Gross Profit</th><th class="r">Expenses</th>
            <th class="r">Net Income</th><th class="r">Margin</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="totals">
            <td class="lbl">Total</td>
            <td class="r">${peso(t.total_revenue)}</td>
            <td class="r muted">${peso(t.cogs)}</td>
            <td class="r">${money(t.gross_profit)}</td>
            <td class="r muted">${peso(t.operating_expenses)}</td>
            <td class="r strong">${money(t.net_income)}</td>
            <td class="r">${pct(t.net_margin)}</td>
          </tr>
        </tfoot>
      </table>
      <div class="notes">
        Accrual basis, posted entries &mdash; reconciles to the income statement. <b>Net Sales</b> = Revenue &minus; Contra Revenue;
        <b>Gross Profit</b> = Net Sales &minus; COGS; <b>Net Income</b> = Gross Profit &minus; Expenses.
        A month can show a loss when COGS is recognized ahead of the related sale.
      </div>
    </section>`;
}

function breakdownSlide(data) {
  const rows = (data.months || [])
    .map(
      (m) => `
      <tr>
        <td class="lbl">${escapeHtml(m.label)}</td>
        <td class="r">${peso(m.sales_revenue)}</td>
        <td class="r">${peso(m.service_revenue)}</td>
        <td class="r muted">${peso(m.other_revenue)}</td>
        <td class="r strong">${peso(m.total_revenue)}</td>
        <td class="r">${peso(m.collections)}</td>
        <td class="c">${num(m.invoices_count)}</td>
        <td class="c">${num(m.units_received)}</td>
        <td class="c">${num(m.repairs_completed)}</td>
        <td class="c">${num(m.quotations_count)}</td>
        <td class="c">${num(m.purchase_orders_count)}</td>
      </tr>`
    )
    .join("");

  const t = data.totals;
  return `
    <section class="slide">
      <h2>Monthly Breakdown</h2>
      <table class="cmp small">
        <thead>
          <tr>
            <th>Month</th><th class="r">Sales</th><th class="r">Services</th>
            <th class="r">Other Rev</th><th class="r">Net Sales</th><th class="r">Collections</th>
            <th class="c">Inv</th><th class="c">Units</th><th class="c">Repairs</th>
            <th class="c">Quotes</th><th class="c">Purchase Orders</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="totals">
            <td class="lbl">Total</td>
            <td class="r">${peso(t.sales_revenue)}</td>
            <td class="r">${peso(t.service_revenue)}</td>
            <td class="r muted">${peso(t.other_revenue)}</td>
            <td class="r strong">${peso(t.total_revenue)}</td>
            <td class="r">${peso(t.collections)}</td>
            <td class="c">${num(t.invoices_count)}</td>
            <td class="c">${num(t.units_received)}</td>
            <td class="c">${num(t.repairs_completed)}</td>
            <td class="c">${num(t.quotations_count)}</td>
            <td class="c">${num(t.purchase_orders_count)}</td>
          </tr>
        </tfoot>
      </table>
      <div class="notes">
        <b>Net Sales</b> = Sales + Services + Other Revenue &minus; discounts. <b>Other Rev</b> includes government tax markup and interest/dividends.
        <b>Collections</b> = cash received that month, including payments on invoices from earlier months &mdash; so it can exceed monthly revenue.
        <b>Purchase Orders</b> = supplier POs raised.
      </div>
      <div class="footer">Generated ${escapeHtml(data.generated_at)}${
    data.generated_by ? " by " + escapeHtml(data.generated_by) : ""
  }</div>
    </section>`;
}

export function buildPerformancePresentation(data) {
  const slides = [
    titleSlide(data),
    kpiSlide(data),
    chartSlide(data),
    profitabilitySlide(data),
    yoySlide(data),
    breakdownSlide(data),
  ].join("\n");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>JMB Computers — Company Performance</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  :root { --blue: ${BLUE}; --teal: ${TEAL}; }
  html, body { height: 100%; }
  body {
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
    background: #0d1b2a; color: #1a1a1a; overflow: hidden;
  }
  .deck { height: 100vh; width: 100vw; position: relative; }
  .slide {
    position: absolute; inset: 0; display: none;
    flex-direction: column; justify-content: center;
    padding: 6vh 8vw; background: #ffffff;
  }
  .slide.active { display: flex; }
  h1 { font-size: 56px; color: var(--blue); margin: 8px 0; }
  h2 { font-size: 38px; color: var(--blue); margin-bottom: 24px; }
  .sub { color: #666; margin-bottom: 16px; font-size: 16px; }

  /* Title slide */
  .slide-title { align-items: flex-start; background:
    linear-gradient(135deg, #ffffff 0%, #e3f2fd 100%); }
  .brand { font-size: 22px; letter-spacing: 3px; color: var(--teal); text-transform: uppercase; font-weight: 700; }
  .subtitle { font-size: 26px; color: #333; margin-top: 8px; }
  .period { font-size: 18px; color: #777; margin-top: 16px; }
  .hero-growth { margin-top: 36px; display: flex; flex-direction: column; gap: 4px; }
  .hero-growth .hero-num { font-size: 64px; font-weight: 800; line-height: 1; }
  .hero-growth .hero-cap { font-size: 16px; color: #667; text-transform: uppercase; letter-spacing: 1px; }
  .hero-growth.up .hero-num { color: #1e7e34; }
  .hero-growth.down .hero-num { color: #c0392b; }
  .hero-growth.neutral { font-size: 18px; color: #889; }
  .hint { position: absolute; bottom: 5vh; left: 8vw; font-size: 14px; color: #999; }

  /* KPI grid */
  .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  .kpi-grid-4 { grid-template-columns: repeat(4, 1fr); gap: 18px; }
  .kpi { background: #f7f9fc; border: 1px solid #e3eaf2; border-left: 6px solid var(--blue);
    border-radius: 10px; padding: 24px; }
  .kpi-grid-4 .kpi { padding: 18px; }
  .kpi-label { font-size: 14px; text-transform: uppercase; letter-spacing: .5px; color: #788; }
  .kpi-value { font-size: 42px; font-weight: 800; color: #16324f; margin: 6px 0; }
  .kpi-grid-4 .kpi-value { font-size: 32px; }
  .kpi-sub { font-size: 13px; color: #667; }
  .kpi-strong { background: #e9f2fb; border-left-color: var(--teal); }
  .kpi-strong .kpi-value { color: var(--blue); }
  .kpi-profit { background: #ecf7ef; border-left-color: #1e7e34; }
  .kpi-profit .kpi-value { color: #1e7e34; }
  .kpi-loss { background: #fdeeec; border-left-color: #c0392b; }
  .kpi-loss .kpi-value { color: #c0392b; }
  .kpi-muted { background: #fbfbfc; border-left-color: #b0b8c2; border-style: dashed; }
  .kpi-muted .kpi-value { color: #6b7480; }
  .callout { margin-top: 28px; font-size: 18px; color: #445; }

  /* Profit & loss strip */
  .neg { color: #c0392b; }
  .pl-strip { display: flex; align-items: stretch; gap: 10px; margin-bottom: 22px; flex-wrap: wrap; }
  .pl-stat { flex: 1; min-width: 120px; background: #f7f9fc; border: 1px solid #e3eaf2;
    border-radius: 8px; padding: 12px 14px; }
  .pl-stat-label { font-size: 12px; text-transform: uppercase; letter-spacing: .4px; color: #788; }
  .pl-stat-value { font-size: 24px; font-weight: 800; color: #16324f; margin: 2px 0; }
  .pl-stat-sub { font-size: 12px; color: #667; }
  .pl-stat.profit { background: #ecf7ef; border-color: #bfe3c8; }
  .pl-stat.profit .pl-stat-value { color: #1e7e34; }
  .pl-stat.loss { background: #fdeeec; border-color: #f3c9c2; }
  .pl-stat.loss .pl-stat-value { color: #c0392b; }
  .pl-op { display: flex; align-items: center; font-size: 24px; color: #aab; font-weight: 700; }

  .badge { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 13px; font-weight: 700; }
  .badge-up { background: #e6f4ea; color: #1e7e34; }
  .badge-down { background: #fdecea; color: #c0392b; }
  .badge-neutral { background: #eef1f4; color: #889; font-weight: 600; }

  /* Bar chart */
  .legend { display: flex; gap: 24px; font-size: 15px; color: #556; margin-bottom: 16px; }
  .swatch { display: inline-block; width: 14px; height: 14px; border-radius: 3px; vertical-align: middle; }
  .chart { flex: 1; display: flex; align-items: flex-end; gap: 1.2vw; padding: 16px 0;
    border-bottom: 2px solid #d8e0ea; min-height: 0; }
  .bar-group { flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; justify-content: flex-end; }
  .bars { flex: 1; width: 100%; display: flex; align-items: flex-end; justify-content: center; gap: 3px; }
  .bar { width: 38%; border-radius: 4px 4px 0 0; min-height: 2px; transition: height .4s ease; }
  .bar-cur { background: var(--blue); }
  .bar-prev { background: #BBDEFB; }
  .bar-label { font-size: 13px; color: #667; margin-top: 8px; }
  .chart-caption { font-size: 14px; color: #889; margin-top: 12px; }

  /* Tables */
  table.cmp { width: 100%; border-collapse: collapse; font-size: 18px; }
  table.cmp.small { font-size: 14px; }
  table.cmp th { text-align: left; color: #556; font-weight: 600; padding: 8px 10px;
    border-bottom: 2px solid #d8e0ea; font-size: .82em; text-transform: uppercase; letter-spacing: .4px; }
  table.cmp td { padding: 7px 10px; border-bottom: 1px solid #eef1f4; }
  table.cmp .r { text-align: right; }
  table.cmp .c { text-align: center; }
  table.cmp .lbl { font-weight: 600; color: #334; }
  table.cmp .muted { color: #889; }
  table.cmp .strong { font-weight: 700; color: #16324f; }
  table.cmp tfoot td { border-top: 2px solid #333; border-bottom: none; font-weight: 700; padding-top: 10px; }
  table.cmp tfoot .totals td { background: #f7f9fc; }
  .notes { margin-top: 14px; font-size: 12.5px; color: #778; line-height: 1.5; }
  .notes b { color: #556; }
  .footer { margin-top: 10px; font-size: 13px; color: #aab; text-align: right; }

  /* Controls */
  .controls { position: fixed; bottom: 18px; right: 18px; display: flex; gap: 8px; z-index: 10; }
  .controls button { background: var(--blue); color: #fff; border: none; border-radius: 8px;
    padding: 10px 16px; font-size: 15px; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,.25); }
  .controls button:hover { background: #0d47a1; }
  .counter { position: fixed; bottom: 22px; left: 18px; color: rgba(255,255,255,.85);
    font-size: 14px; z-index: 10; background: rgba(0,0,0,.25); padding: 6px 12px; border-radius: 8px; }

  @media print {
    @page { size: landscape; margin: 0; }
    body { background: #fff; overflow: visible; }
    .deck { height: auto; }
    .slide { display: flex !important; position: relative; inset: auto;
      height: 100vh; page-break-after: always; border-bottom: 1px solid #eee; }
    .controls, .counter, .hint { display: none !important; }
  }
</style>
</head>
<body>
  <div class="deck" id="deck">
    ${slides}
  </div>
  <div class="counter" id="counter"></div>
  <div class="controls">
    <button onclick="window.__prev()">◀ Prev</button>
    <button onclick="window.__next()">Next ▶</button>
    <button onclick="window.__fs()">Present ⛶</button>
    <button onclick="window.print()">Print / PDF</button>
  </div>
<script>
  (function () {
    var slides = Array.prototype.slice.call(document.querySelectorAll(".slide"));
    var i = 0;
    var counter = document.getElementById("counter");
    function show(n) {
      i = Math.max(0, Math.min(slides.length - 1, n));
      slides.forEach(function (s, idx) { s.classList.toggle("active", idx === i); });
      counter.textContent = (i + 1) + " / " + slides.length;
    }
    window.__next = function () { show(i + 1); };
    window.__prev = function () { show(i - 1); };
    window.__fs = function () {
      if (document.fullscreenElement) document.exitFullscreen();
      else document.documentElement.requestFullscreen();
    };
    document.addEventListener("keydown", function (e) {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { window.__next(); e.preventDefault(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { window.__prev(); e.preventDefault(); }
      else if (e.key === "Home") { show(0); }
      else if (e.key === "End") { show(slides.length - 1); }
      else if (e.key === "f" || e.key === "F") { window.__fs(); }
    });
    show(0);
  })();
</script>
</body>
</html>`;
}
